import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { patchAdminSearch } from '../../../utils/adminDashboardNav';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaFolderOpen,
  FaList,
  FaPlus,
  FaSpinner,
  FaSync,
  FaTh,
  FaThLarge,
  FaTrash,
  FaEye,
  FaEdit,
} from 'react-icons/fa';
import '../styles/ProjectSection.css';
import AddProject from './AddProject';
import EditProject from './EditProject';
import ProjectDetailView from './ProjectDetailView';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  collectTeamMembers,
  computeNextService,
  computeProgress,
  formatDate,
  isOverdue,
  mapStatusToDisplay,
  resolveProjectServiceLabel,
  formatRecurrenceFrequencyLabel,
} from '../utils/projectUtils';

const PAGE_SIZE = 5;

const PROJECT_TABS = [
  'Overview',
  'Tasks',
  'Team',
  'Files',
  'Invoices',
  'Activity',
];

function projectIdFromSearch(search) {
  return (
    new URLSearchParams(String(search || '').replace(/^\?/, '')).get(
      'project',
    ) || ''
  );
}

function projectTabFromSearch(search) {
  const raw =
    new URLSearchParams(String(search || '').replace(/^\?/, '')).get('tab') ||
    'Overview';
  return PROJECT_TABS.includes(raw) ? raw : 'Overview';
}

const FIND_ALL_PROJECTS = `
  query FindAllProject($limit: Int) {
    findAllProject(limit: $limit) {
      id
      projectId
      name
      startDate
      deadLine
      status
      createdAt
      recurrenceFrequency
      recurrenceActive
      nextVisitAt
      seriesRootId
      billingCadence
      booking {
        id
        fullName
        estimatedPRice
      }
      manager {
        id
        firstName
        lastName
      }
      projectServices
      tasks {
        id
        status
      }
    }
  }
`;

const FIND_PROJECT_QUOTATIONS = `
  query FindBookingQuotation($bookingId: String!) {
    findBookingQuotation(bookingId: $bookingId) {
      id
      quotationNumber
      versionNumber
      projectId
      quotationStage
    }
  }
`;

function uniqueDraftQuoteLabels(rows, projectId) {
  const seen = new Set();
  const labels = [];
  for (const quote of rows || []) {
    if (String(quote?.projectId || '') !== String(projectId || '')) continue;
    if (String(quote?.quotationStage || '').toUpperCase() !== 'DRAFT') continue;
    const number = String(quote?.quotationNumber || '').trim();
    if (!number || seen.has(number)) continue;
    seen.add(number);
    const version = Number(quote?.versionNumber || 1);
    labels.push(version > 1 ? `${number} (rev ${version})` : number);
  }
  return labels;
}

function projectStatusKey(proj) {
  const raw = String(proj?.dbStatus || proj?.status || '')
    .trim()
    .toLowerCase();
  if (raw === 'draft' || raw === 'on track') return 'pending';
  return raw;
}

function canDeleteProject(proj) {
  return projectStatusKey(proj) === 'pending';
}

function projectDeleteTitle(proj) {
  if (canDeleteProject(proj)) return 'Delete';
  if (projectStatusKey(proj) === 'active') {
    return 'Active projects cannot be deleted';
  }
  return 'Only draft projects can be deleted';
}

export default function ProjectSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('list');
  const [page, setPage] = useState(1);
  const [viewProjectId, setViewProjectId] = useState(
    () => projectIdFromSearch(location.search) || null,
  );
  const [viewProjectTab, setViewProjectTab] = useState(() =>
    projectTabFromSearch(location.search),
  );
  const [editProjectId, setEditProjectId] = useState(
    () =>
      new URLSearchParams(location.search || '').get('edit') || null,
  );
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(
    () =>
      new URLSearchParams(location.search || '').get('action') === 'add',
  );
  const [sourceContractId, setSourceContractId] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const openProject = (id, tab = 'Overview') => {
    const nextTab = PROJECT_TABS.includes(tab) ? tab : 'Overview';
    setViewProjectTab(nextTab);
    setViewProjectId(id);
    navigate({
      pathname: location.pathname || '/',
      search: patchAdminSearch(
        location.search,
        { page: 'project', project: id, tab: nextTab },
        ['invoice', 'edit', 'action'],
      ),
    });
  };

  const closeProject = () => {
    setViewProjectId(null);
    setViewProjectTab('Overview');
    navigate(
      {
        pathname: location.pathname || '/',
        search: patchAdminSearch(location.search, {}, [
          'project',
          'tab',
          'invoice',
        ]),
      },
      { replace: true },
    );
  };

  const openEditProject = (id) => {
    setEditProjectId(id);
    navigate({
      pathname: location.pathname || '/',
      search: patchAdminSearch(
        location.search,
        { page: 'project', edit: id },
        ['project', 'tab', 'invoice', 'action'],
      ),
    });
  };

  const closeEditProject = () => {
    setEditProjectId(null);
    navigate(
      {
        pathname: location.pathname || '/',
        search: patchAdminSearch(location.search, {}, ['edit']),
      },
      { replace: true },
    );
  };

  const openAddProject = () => {
    setIsAddProjectOpen(true);
    navigate({
      pathname: location.pathname || '/',
      search: patchAdminSearch(
        location.search,
        { page: 'project', action: 'add' },
        ['project', 'tab', 'invoice', 'edit'],
      ),
    });
  };

  const closeAddProject = () => {
    setIsAddProjectOpen(false);
    setSourceContractId('');
    navigate(
      {
        pathname: location.pathname || '/',
        search: patchAdminSearch(location.search, {}, ['action', 'contractId']),
      },
      { replace: true },
    );
  };
  /** Roots in this set are collapsed; visits stay open by default. */
  const [collapsedRoots, setCollapsedRoots] = useState(() => new Set());

  const toggleRootExpanded = (rootId) => {
    setCollapsedRoots((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await graphqlRequest(FIND_ALL_PROJECTS, { limit: 150 });
      const fetchedProjects = data?.findAllProject || [];

      const formattedProjects = fetchedProjects.map((proj) => {
        const progress = computeProgress(proj);
        const displayStatus = mapStatusToDisplay(proj.status);
        const serviceLabel = resolveProjectServiceLabel(proj);
        return {
          id: proj.id,
          projectId: proj.projectId || 'N/A',
          title: proj.name || 'N/A',
          client: proj.booking?.fullName || 'N/A',
          bookingId: proj.booking?.id,
          startDate: formatDate(proj.startDate),
          deadline: formatDate(proj.deadLine),
          status: displayStatus,
          dbStatus: proj.status,
          progress,
          nextService: computeNextService(proj),
          serviceLabel,
          overdue: isOverdue(proj),
          team: collectTeamMembers(proj),
          createdAt: proj.createdAt,
          seriesRootId: proj.seriesRootId || null,
          recurrenceFrequency: proj.recurrenceFrequency || null,
          recurrenceActive: proj.recurrenceActive,
          nextVisitAt: proj.nextVisitAt,
          billingCadence: proj.billingCadence || null,
        };
      });

      setProjects(formattedProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
      toast.error(err?.message || 'Failed to fetch projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const pid = params.get('project') || '';
    const tab = projectTabFromSearch(location.search);
    setViewProjectId(pid || null);
    setViewProjectTab(tab);
    setEditProjectId(pid ? null : params.get('edit') || null);
    if (params.get('action') === 'add') {
      setSourceContractId(params.get('contractId') || '');
      setIsAddProjectOpen(true);
    } else {
      setIsAddProjectOpen(false);
    }
  }, [location.search]);

  const stats = useMemo(() => {
    const total = projects.length;
    const ongoing = projects.filter((p) => p.status === 'Active').length;
    const completed = projects.filter((p) => p.status === 'Complete').length;
    const overdue = projects.filter((p) => p.overdue).length;
    return { total, ongoing, completed, overdue };
  }, [projects]);

  const filteredProjects = useMemo(() => {
    let list = [...projects];

    if (statusFilter !== 'All') {
      list = list.filter((p) => p.status === statusFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [p.projectId, p.title, p.client, p.status].some((v) =>
          String(v).toLowerCase().includes(q),
        ),
      );
    }

    list.sort((a, b) => {
      if (sortBy === 'oldest') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      if (sortBy === 'deadline') {
        return (a.deadline || '').localeCompare(b.deadline || '');
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return list;
  }, [projects, statusFilter, searchQuery, sortBy]);

  /** Top-level rows only (roots + orphan visits); children hang under roots. */
  const { topLevelProjects, childrenByRoot } = useMemo(() => {
    const idSet = new Set(filteredProjects.map((p) => p.id));
    const allById = new Map(projects.map((p) => [p.id, p]));
    const childrenByRootMap = new Map();
    const tops = [];

    for (const p of filteredProjects) {
      if (!p.seriesRootId) {
        tops.push(p);
        continue;
      }
      // Visit whose root is not in the filtered set: still nest under root if root exists in full list
      // and we include the root as a top row when any child matches filters.
      if (!idSet.has(p.seriesRootId) && allById.has(p.seriesRootId)) {
        // Will attach under root when we inject roots below
      }
      const list = childrenByRootMap.get(p.seriesRootId) || [];
      list.push(p);
      childrenByRootMap.set(p.seriesRootId, list);
    }

    // If a visit matched filters but its root did not, add the root as a top-level row.
    for (const rootId of childrenByRootMap.keys()) {
      if (!idSet.has(rootId) && allById.has(rootId)) {
        tops.push(allById.get(rootId));
        idSet.add(rootId);
      }
    }

    // Remove visit rows from tops (they only appear nested).
    const topOnly = tops.filter((p) => !p.seriesRootId);

    // Orphan visits (root missing entirely) stay as top-level.
    for (const p of filteredProjects) {
      if (p.seriesRootId && !allById.has(p.seriesRootId)) {
        topOnly.push(p);
      }
    }

    for (const [, list] of childrenByRootMap) {
      list.sort(
        (a, b) =>
          new Date(b.startDate || b.createdAt || 0) -
          new Date(a.startDate || a.createdAt || 0),
      );
    }

    topOnly.sort((a, b) => {
      if (sortBy === 'oldest') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      if (sortBy === 'deadline') {
        return (a.deadline || '').localeCompare(b.deadline || '');
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return { topLevelProjects: topOnly, childrenByRoot: childrenByRootMap };
  }, [filteredProjects, projects, sortBy]);

  const totalPages = Math.max(
    1,
    Math.ceil(topLevelProjects.length / PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const paginatedProjects = topLevelProjects.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchQuery, sortBy]);

  const handleDelete = async (projOrId) => {
    const proj =
      projOrId && typeof projOrId === 'object'
        ? projOrId
        : projects.find((row) => row.id === projOrId);
    if (!proj?.id) return;
    if (!canDeleteProject(proj)) {
      toast.error(projectDeleteTitle(proj));
      return;
    }

    const code = proj.projectId || proj.title || proj.name || 'this project';
    const bookingId = proj.bookingId || proj.booking?.id;
    let draftQuotes = [];
    if (bookingId) {
      try {
        const res = await graphqlRequest(FIND_PROJECT_QUOTATIONS, {
          bookingId,
        });
        draftQuotes = uniqueDraftQuoteLabels(
          res?.findBookingQuotation,
          proj.id,
        );
      } catch (err) {
        console.warn('Could not load linked quotations before delete', err);
      }
    }

    let message = `Delete project ${code}?`;
    if (draftQuotes.length === 1) {
      message = `Delete project ${code}? Linked draft quotation ${draftQuotes[0]} will also be deleted.`;
    } else if (draftQuotes.length > 1) {
      message = `Delete project ${code}? Linked draft quotations ${draftQuotes.join(', ')} will also be deleted.`;
    }

    if (!(await appConfirmMessage(message))) return;
    try {
      const mutation = `
        mutation RemoveProject {
          removeProject(id: "${proj.id}") {
            id
          }
        }
      `;
      await graphqlRequest(mutation);
      setProjects((prev) => prev.filter((p) => p.id !== proj.id));
      toast.success(
        draftQuotes.length
          ? `Project ${code} and quotation ${draftQuotes.join(', ')} deleted`
          : 'Project deleted',
      );
    } catch (err) {
      console.error('Error deleting project:', err);
      toast.error(err?.message || 'Failed to delete project');
    }
  };

  const statusClass = (status) => {
    const key = String(status).toLowerCase().replace(/\s+/g, '-');
    return `project-status project-status--${key}`;
  };

  const rangeStart =
    topLevelProjects.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, topLevelProjects.length);

  if (viewProjectId) {
    return (
      <ProjectDetailView
        projectId={viewProjectId}
        initialTab={viewProjectTab}
        onBack={closeProject}
      />
    );
  }

  return (
    <div className="project-page">
      <div className="project-kpi-grid">
        <article className="project-kpi-card">
          <div className="project-kpi-icon project-kpi-icon--total">
            <FaFolderOpen />
          </div>
          <div>
            <p className="project-kpi-label">Total Projects</p>
            <p className="project-kpi-value">{stats.total}</p>
          </div>
        </article>
        <article className="project-kpi-card">
          <div className="project-kpi-icon project-kpi-icon--ongoing">
            <FaSpinner />
          </div>
          <div>
            <p className="project-kpi-label">Active</p>
            <p className="project-kpi-value">{stats.ongoing}</p>
          </div>
        </article>
        <article className="project-kpi-card">
          <div className="project-kpi-icon project-kpi-icon--completed">
            <FaCheckCircle />
          </div>
          <div>
            <p className="project-kpi-label">Completed</p>
            <p className="project-kpi-value">{stats.completed}</p>
          </div>
        </article>
        <article className="project-kpi-card">
          <div className="project-kpi-icon project-kpi-icon--overdue">
            <FaExclamationTriangle />
          </div>
          <div>
            <p className="project-kpi-label">Overdue</p>
            <p className="project-kpi-value">{stats.overdue}</p>
          </div>
        </article>
      </div>

      <div className="project-panel">
        <div className="project-panel-header">
          <h2 className="project-title">Projects</h2>
          <button
            type="button"
            className="add-project-btn"
            onClick={openAddProject}
          >
            <FaPlus /> Create Project
          </button>
        </div>

        <div className="project-toolbar">
          <div className="project-toolbar-filters">
            <label className="project-filter">
              <span>Status</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Complete">Complete</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>

            <label className="project-filter project-filter--search">
              <input
                type="search"
                placeholder="Search by project, client, or ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>

            <label className="project-filter">
              <span>Sort by</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="title">Title A–Z</option>
                <option value="deadline">Deadline</option>
              </select>
            </label>
          </div>

          <div
            className="project-view-toggle"
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <FaList />
            </button>
            <button
              type="button"
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <FaTh />
            </button>
            <button
              type="button"
              className={viewMode === 'compact' ? 'active' : ''}
              onClick={() => setViewMode('compact')}
              title="Compact view"
            >
              <FaThLarge />
            </button>
            <button
              type="button"
              onClick={fetchProjects}
              title="Refresh"
              disabled={loading}
            >
              <FaSync />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="project-loading">Loading projects...</div>
        ) : topLevelProjects.length === 0 ? (
          <div className="project-empty">No projects found.</div>
        ) : viewMode === 'grid' ? (
          <div className="project-grid">
            {paginatedProjects.map((proj) => {
              const visits = !proj.seriesRootId
                ? childrenByRoot.get(proj.id) || []
                : [];
              return (
              <article key={proj.id} className="project-card">
                <div className="project-card-top">
                  <span className="project-card-id">{proj.projectId}</span>
                  <span className={statusClass(proj.status)}>
                    {proj.status}
                  </span>
                </div>
                <h3>
                  {proj.title}
                  {visits.length > 0 ? (
                    <span className="project-visit-count">
                      {' '}
                      · {visits.length} visit{visits.length === 1 ? '' : 's'}
                    </span>
                  ) : null}
                  {proj.seriesRootId ? (
                    <span className="project-visit-chip"> Visit</span>
                  ) : null}
                  {String(proj.billingCadence || '').toLowerCase() ===
                  'monthly' ? (
                    <span className="project-visit-chip"> Monthly bill</span>
                  ) : null}
                  {formatRecurrenceFrequencyLabel(proj.recurrenceFrequency) ? (
                    <span className="project-visit-chip">
                      {' '}
                      {formatRecurrenceFrequencyLabel(proj.recurrenceFrequency)}
                    </span>
                  ) : null}
                </h3>
                <p className="project-card-client">{proj.client}</p>
                <div className="project-progress">
                  <div className="project-progress-bar">
                    <div
                      className="project-progress-fill"
                      style={{ width: `${proj.progress.percent}%` }}
                    />
                  </div>
                  <span className="project-progress-text">
                    {proj.progress.percent}% — {proj.progress.done} of{' '}
                    {proj.progress.total} {proj.progress.unit || 'services'} done
                  </span>
                </div>
                <p className="project-card-meta">Next: {proj.nextService}</p>
                <p className="project-card-meta">Services: {proj.serviceLabel}</p>
                {visits.length > 0 ? (
                  <ul className="project-card-visits">
                    {visits.map((v) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          className="project-card-visit-link"
                          onClick={() => openProject(v.id)}
                        >
                          {v.title || 'Visit'}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="project-card-actions">
                  {proj.bookingId ? (
                    <button
                      type="button"
                      className="project-custom-invoice-btn"
                      onClick={() => openProject(proj.id, 'Invoices')}
                    >
                      Custom invoice
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="project-action-btn project-action-btn--view"
                    title="View"
                    onClick={() => openProject(proj.id)}
                  >
                    <FaEye />
                  </button>
                  <button
                    type="button"
                    className="project-action-btn project-action-btn--edit"
                    title="Edit"
                    onClick={() => openEditProject(proj.id)}
                  >
                    <FaEdit color="#fff" />
                  </button>
                  <button
                    type="button"
                    className="project-action-btn project-action-btn--delete"
                    title={projectDeleteTitle(proj)}
                    disabled={!canDeleteProject(proj)}
                    onClick={() => handleDelete(proj)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </article>
              );
            })}
          </div>
        ) : (
          <div className="project-table-container">
            <table
              className={`project-table ${viewMode === 'compact' ? 'project-table--compact' : ''}`}
            >
              <thead>
                <tr>
                  <th>Project ID</th>
                  <th>Project Title</th>
                  <th>Client</th>
                  <th>Progress</th>
                  <th>Next Service</th>
                  <th>Services</th>
                  <th>Status</th>
                  <th>Team</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProjects.map((proj) => {
                  const visits = !proj.seriesRootId
                    ? childrenByRoot.get(proj.id) || []
                    : [];
                  const expanded =
                    visits.length > 0 && !collapsedRoots.has(proj.id);

                  const renderProjectRow = (row, { nested = false } = {}) => (
                    <tr
                      key={row.id}
                      className={nested ? 'project-visit-row' : undefined}
                    >
                      <td className="project-id-cell">
                        <div className="project-id-with-toggle">
                          {!nested && visits.length > 0 ? (
                            <button
                              type="button"
                              className="project-expand-btn"
                              aria-expanded={expanded}
                              aria-label={
                                expanded ? 'Collapse visits' : 'Expand visits'
                              }
                              onClick={() => toggleRootExpanded(row.id)}
                            >
                              {expanded ? '▾' : '▸'}
                            </button>
                          ) : (
                            <span className="project-expand-spacer" />
                          )}
                          <span>{nested ? proj.projectId : row.projectId}</span>
                        </div>
                      </td>
                      <td className="project-title-cell">
                        <div className="project-title-inner">
                          <span>{row.title}</span>
                          {row.seriesRootId ? (
                            <span className="project-visit-chip">Visit</span>
                          ) : null}
                          {String(row.billingCadence || '').toLowerCase() ===
                          'monthly' ? (
                            <span className="project-visit-chip">
                              Monthly bill
                            </span>
                          ) : null}
                          {formatRecurrenceFrequencyLabel(
                            row.recurrenceFrequency,
                          ) ? (
                            <span className="project-visit-chip">
                              {formatRecurrenceFrequencyLabel(
                                row.recurrenceFrequency,
                              )}
                            </span>
                          ) : null}
                          {!nested && visits.length > 0 ? (
                            <span className="project-visit-count">
                              {visits.length} visit
                              {visits.length === 1 ? '' : 's'}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <button type="button" className="project-client-link">
                          {row.client}
                        </button>
                      </td>
                      <td className="project-progress-cell">
                        <div className="project-progress">
                          <div className="project-progress-bar">
                            <div
                              className="project-progress-fill"
                              style={{ width: `${row.progress.percent}%` }}
                            />
                          </div>
                          <span className="project-progress-text">
                            {row.progress.percent}% — {row.progress.done} of{' '}
                            {row.progress.total} {row.progress.unit || 'services'}{' '}
                            done
                          </span>
                        </div>
                      </td>
                      <td>{row.nextService}</td>
                      <td>{row.serviceLabel}</td>
                      <td>
                        <span className={statusClass(row.status)}>
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <div className="project-team">
                          {row.team.length > 0 ? (
                            row.team.map((member) => (
                              <span
                                key={member.id}
                                className="project-team-avatar"
                                title={member.name}
                              >
                                {member.initials}
                              </span>
                            ))
                          ) : (
                            <span className="project-team-empty">—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="project-actions">
                          {row.bookingId ? (
                            <button
                              type="button"
                              className="project-custom-invoice-btn"
                              onClick={() => openProject(row.id, 'Invoices')}
                            >
                              Custom invoice
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="project-action-btn project-action-btn--view"
                            title="View"
                            onClick={() => openProject(row.id)}
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            className="project-action-btn project-action-btn--edit"
                            title="Edit"
                            onClick={() => openEditProject(row.id)}
                          >
                            <FaEdit color="#fff" />
                          </button>
                          <button
                            type="button"
                            className="project-action-btn project-action-btn--delete"
                            title={projectDeleteTitle(row)}
                            disabled={!canDeleteProject(row)}
                            onClick={() => handleDelete(row)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );

                  return (
                    <React.Fragment key={proj.id}>
                      {renderProjectRow(proj)}
                      {expanded
                        ? visits.map((v) =>
                            renderProjectRow(v, { nested: true }),
                          )
                        : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && topLevelProjects.length > 0 && (
          <div className="project-pagination">
            <span>
              Showing {rangeStart}-{rangeEnd} of {topLevelProjects.length}
            </span>
            <div className="project-pagination-controls">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={n === currentPage ? 'active' : ''}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {editProjectId && (
        <EditProject
          projectId={editProjectId}
          onClose={closeEditProject}
          onSaved={fetchProjects}
        />
      )}

      {isAddProjectOpen && (
        <AddProject
          sourceContractId={sourceContractId}
          onClose={() => {
            closeAddProject();
            fetchProjects();
          }}
        />
      )}
    </div>
  );
}
